import type { Message } from '../shared/types';

/**
 * A message belongs on the right only when the author is this browser.
 * `message.author.self` is server-wide, so clients on the same server used to
 * see each other's messages as their own; the client identity settles it.
 */
export function isOwnMessage(message: Message, meId: string, meshPeerId?: string): boolean {
  const author = message.author;
  const mine = clientIdOf(meId);
  if (author.id === meId || author.id === mine) return true;
  // Assistant replies belong on the side of the person who prompted them.
  if (author.bot && author.self) return true;
  // Author ids from another browser can never be mine, whatever `self` says.
  if (author.id.startsWith('client:')) return false;
  if (meshPeerId && author.id === meshPeerId) return Boolean(author.self);
  // A mesh author id that is not this peer is somebody else, even if the old
  // server-wide `self` flag said otherwise.
  if (meshPeerId && message.platform === 'mesh') return false;
  // Legacy server messages ('me'), bridge echoes of the linked account and
  // pre-identity mesh messages only exist without a client-scoped author.
  return Boolean(author.self);
}

/** Server-scoped prefix so two browsers on one backend get distinct author ids. */
export function clientIdOf(meId: string): string {
  return meId.startsWith('client:') ? meId : `client:${meId}`;
}

/**
 * Unread follows the same ownership rule as the bubble side, so another
 * browser's message on this backend still counts even though it arrived
 * through the local server (`via: 'local'`).
 */
export function isUnreadIncoming(
  message: Message,
  meId: string,
  options: { meshPeerId?: string; activeId?: string | null; known?: boolean } = {},
): boolean {
  if (options.known) return false;
  if (options.activeId === message.conversationId) return false;
  return !isOwnMessage(message, meId, options.meshPeerId);
}
